import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { Users, CheckCircle, XCircle, Clock, Trash, QrCode, Copy, Printer, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrgMembersPage() {
  const { activeOrg } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orgs/${activeOrg.id}/members`, { withCredentials: true });
      setMembers(data.members || []);
    } catch { toast.error("Failed to load members"); }
    setLoading(false);
  }, [activeOrg]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const approveMember = async (mem) => {
    try {
      await axios.post(`${API}/orgs/${activeOrg.id}/memberships/${mem.id}/approve`, {}, { withCredentials: true });
      toast.success(`${mem.user?.name} approved`);
      fetchMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rejectMember = async (mem) => {
    const reason = window.prompt("Reason (optional, sent to applicant):") || "";
    try {
      await axios.post(`${API}/orgs/${activeOrg.id}/memberships/${mem.id}/reject`, { reason }, { withCredentials: true });
      toast.success("Rejected");
      fetchMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeMember = async (mem) => {
    if (!window.confirm(`Remove ${mem.user?.name} from ${activeOrg.name}?`)) return;
    try {
      await axios.delete(`${API}/orgs/${activeOrg.id}/memberships/${mem.id}`, { withCredentials: true });
      toast.success("Removed");
      fetchMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!activeOrg) {
    return <div className="max-w-7xl mx-auto px-4 py-8"><p className="text-zinc-500">No active organization</p></div>;
  }

  const pending = members.filter(m => m.status === "pending");
  const active = members.filter(m => m.status === "active");
  const rejected = members.filter(m => m.status === "rejected");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="org-members-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{activeOrg.name} · {activeOrg.code}</p>
          <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Team Members</h1>
          <p className="text-sm text-zinc-500 mt-1">{active.length} active · {pending.length} pending</p>
        </div>
        <button onClick={() => setShowQr(true)} className="px-4 py-2 text-sm font-semibold bg-[#0055FF] text-white hover:bg-[#0044CC] flex items-center gap-2" data-testid="invite-team-btn">
          <QrCode size={16} weight="bold" /> Invite Team
        </button>
      </div>

      {loading ? <p className="font-mono text-sm text-zinc-500 text-center py-12">LOADING...</p> : (
        <>
          {pending.length > 0 && (
            <section className="mb-8" data-testid="pending-members-section">
              <h2 className="font-[Chivo] text-lg font-bold text-zinc-950 mb-3 flex items-center gap-2"><Clock size={18} className="text-amber-500" /> Pending Requests ({pending.length})</h2>
              <div className="border border-amber-200 bg-amber-50/30 divide-y divide-amber-100">
                {pending.map(m => (
                  <div key={m.id} className="p-4 flex items-center justify-between" data-testid={`pending-member-${m.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-zinc-950">{m.user?.name}</p>
                      <p className="text-xs text-zinc-500 font-mono">{m.user?.email} · {m.user?.phone || "no phone"}</p>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-1">requested {new Date(m.requested_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => approveMember(m)} className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1" data-testid={`approve-member-${m.id}`}>
                        <CheckCircle size={14} weight="fill" /> Approve
                      </button>
                      <button onClick={() => rejectMember(m)} className="px-3 py-1.5 text-xs font-semibold border border-zinc-300 text-zinc-700 hover:bg-white" data-testid={`reject-member-${m.id}`}>
                        <XCircle size={14} className="inline mr-1" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section data-testid="active-members-section">
            <h2 className="font-[Chivo] text-lg font-bold text-zinc-950 mb-3 flex items-center gap-2"><Users size={18} className="text-[#0055FF]" /> Active Members ({active.length})</h2>
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              {active.length === 0 ? <p className="p-6 text-sm text-zinc-500 text-center">No active members yet</p> :
                active.map(m => (
                  <div key={m.id} className="p-4 flex items-center justify-between" data-testid={`active-member-${m.id}`}>
                    <div className="flex items-center gap-3">
                      {m.role === "org_admin" && <Crown size={16} weight="fill" className="text-amber-500" />}
                      <div>
                        <p className="text-sm font-semibold text-zinc-950">{m.user?.name}</p>
                        <p className="text-xs text-zinc-500 font-mono">{m.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{m.role.replace("_", " ")}</span>
                      <button onClick={() => removeMember(m)} className="p-1 text-zinc-400 hover:text-red-500" title="Remove" data-testid={`remove-member-${m.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))
              }
            </div>
          </section>

          {rejected.length > 0 && (
            <section className="mt-8" data-testid="rejected-members-section">
              <h2 className="font-[Chivo] text-sm font-bold text-zinc-500 mb-2 uppercase tracking-wider">Rejected ({rejected.length})</h2>
              <div className="border border-zinc-200 divide-y divide-zinc-100 opacity-60">
                {rejected.map(m => (
                  <div key={m.id} className="p-3 flex items-center justify-between text-xs">
                    <div>
                      <p className="text-zinc-700">{m.user?.name}</p>
                      <p className="font-mono text-zinc-400">{m.user?.email}</p>
                    </div>
                    {m.rejection_reason && <p className="text-zinc-500 italic">"{m.rejection_reason}"</p>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* QR / Invite Dialog */}
      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="sm:max-w-md" data-testid="invite-qr-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Invite Team — {activeOrg.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 print:p-8">
            <p className="text-sm text-zinc-600">Show this QR or share the join link with your team. They land on a pre-filled join form.</p>
            <div className="flex flex-col items-center bg-white border border-zinc-200 p-6">
              <QRCodeSVG value={`${window.location.origin}/login?join=${activeOrg.code}`} size={220} level="M" marginSize={2} />
              <p className="font-mono text-2xl font-bold text-zinc-950 mt-4 tracking-widest">{activeOrg.code}</p>
              <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">{activeOrg.name}</p>
            </div>
            <div className="flex gap-2 print:hidden">
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/login?join=${activeOrg.code}`); toast.success("Link copied"); }} className="flex-1 px-4 py-2 text-sm bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center gap-2">
                <Copy size={14} /> Copy Link
              </button>
              <button onClick={() => window.print()} className="flex-1 px-4 py-2 text-sm bg-[#0055FF] text-white hover:bg-[#0044CC] flex items-center justify-center gap-2">
                <Printer size={14} /> Print
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
